import * as THREE from 'three'

const updates = new WeakMap<THREE.Object3D, (preserveTipContacts: boolean) => void>()

/** A continuous membrane follows the existing named segment rig. The rig keeps its
 * original transforms and endpoint/contact contract; only its visual skin changes. */
export function installTentacleSurface(model: THREE.Group): () => void {
  const strands = new Map<number, THREE.Mesh[]>()
  model.traverse((part) => {
    const match = /^(JK|ZP)_Tent_(\d+)_(\d+)$/.exec(part.name)
    if (!match || !(part instanceof THREE.Mesh)) return
    const index = Number(match[2])
    const strand = strands.get(index) ?? []
    strand[Number(match[3])] = part
    strands.set(index, strand)
  })
  if (!strands.size) return () => {}
  const chains = [...strands.values()]
  const rings = 64, sides = 10, verticesPerStrand = (rings + 1) * (sides + 1)
  const positions = new Float32Array(chains.length * verticesPerStrand * 3)
  const normals = new Float32Array(positions.length)
  const uvs = new Float32Array(chains.length * verticesPerStrand * 2)
  const indices: number[] = []
  for (let k = 0; k < chains.length; k++) {
    for (let j = 0; j <= rings; j++) for (let i = 0; i <= sides; i++) {
      const n = k * verticesPerStrand + j * (sides + 1) + i
      uvs[n * 2] = i / sides; uvs[n * 2 + 1] = j / rings
      if (j < rings && i < sides) indices.push(n, n + 1, n + sides + 1, n + 1, n + sides + 2, n + sides + 1)
    }
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage))
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3).setUsage(THREE.DynamicDrawUsage))
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
  geometry.setIndex(indices)
  const surface = new THREE.Mesh(geometry, chains[0][0].material)
  surface.name = 'ContinuousTentacleMembrane'
  surface.castShadow = true
  surface.receiveShadow = true
  surface.frustumCulled = false
  model.add(surface)
  const visibility = chains.flat().map((part) => [part, part.visible] as const)
  visibility.forEach(([part]) => { part.visible = false })
  const inverse = new THREE.Matrix4(), localMatrix = new THREE.Matrix4()
  const tangent = new THREE.Vector3(), radial = new THREE.Vector3(), binormal = new THREE.Vector3()
  const point = new THREE.Vector3(), result = new THREE.Vector3(), reference = new THREE.Vector3(0, 0, 1)
  const curves = chains.map((chain) => new THREE.CatmullRomCurve3(Array.from({ length: chain.length + 2 }, () => new THREE.Vector3())))
  const update = (preserveTipContacts: boolean) => {
    model.updateWorldMatrix(true, true)
    inverse.copy(model.matrixWorld).invert()
    chains.forEach((chain, k) => {
      const curve = curves[k]
      for (let j = 0; j < chain.length; j++) {
        localMatrix.multiplyMatrices(inverse, chain[j].matrixWorld)
        curve.points[j + 1].set(0, 0, 0).applyMatrix4(localMatrix)
        if (j === 0) curve.points[0].set(0, 1, 0).applyMatrix4(localMatrix)
        if (j === chain.length - 1) curve.points[j + 2].set(0, -1, 0).applyMatrix4(localMatrix)
      }
      // Thickness is authored in model units, independent of diorama normalization.
      localMatrix.multiplyMatrices(inverse, chain[0].matrixWorld)
      const startRadius = new THREE.Vector3().setFromMatrixScale(localMatrix).x * 1.1
      for (let j = 0; j <= rings; j++) {
        const t = j / rings
        const visibleLength = preserveTipContacts ? 1 : 0.76 + 0.24 * (0.5 + 0.5 * Math.sin(k * 2.4 + 0.9))
        curve.getPoint(t * visibleLength, point)
        curve.getTangent(t * visibleLength, tangent)
        radial.crossVectors(tangent, reference).normalize()
        if (radial.lengthSq() < 0.01) radial.set(1, 0, 0)
        binormal.crossVectors(tangent, radial).normalize()
        const taperEnd = 0.84 + 0.15 * (0.5 + 0.5 * Math.sin(k * 2.4))
        const taper = Math.max(0.018, 1 - Math.pow(t / taperEnd, 1.6))
        const radius = startRadius * Math.min(1, t * 10 + 0.03) * taper
        for (let i = 0; i <= sides; i++) {
          const a = i / sides * Math.PI * 2
          result.copy(radial).multiplyScalar(Math.cos(a)).addScaledVector(binormal, Math.sin(a))
          const n = (k * verticesPerStrand + j * (sides + 1) + i) * 3
          result.toArray(normals, n)
          result.multiplyScalar(radius).add(point).toArray(positions, n)
        }
      }
    })
    geometry.attributes.position.needsUpdate = true
    geometry.attributes.normal.needsUpdate = true
  }
  updates.set(model, update)
  update(false)
  return () => {
    updates.delete(model)
    surface.removeFromParent()
    geometry.dispose()
    visibility.forEach(([part, visible]) => { part.visible = visible })
  }
}

export function updateTentacleSurface(model: THREE.Group, preserveTipContacts = true): void {
  updates.get(model)?.(preserveTipContacts)
}
